/**
 * "The email we sent" — opens the exact message one recipient received (#1545).
 *
 * Two decisions worth knowing before changing anything here.
 *
 * **It shows the stored message or nothing.** There is deliberately no fallback
 * that re-renders from the current template. The email recipe, the branding and
 * the office's own wording all drift, so a re-render would show TODAY'S email in
 * place of the one this customer actually received — and the office would
 * reasonably act on it ("I definitely told them the valve failed"). A send made
 * before Groundwork started keeping copies says exactly that instead.
 *
 * **The body renders in a sandboxed iframe.** It is HTML we composed, but it
 * carries office-authored text and customer-supplied names, and it is replayed
 * long after the fact. A sandbox with no `allow-scripts` and no
 * `allow-same-origin` means the worst case for anything that ever got into a
 * stored body is inert markup: no script, no access to this page, no network
 * identity. `srcdoc` keeps it out of the app's own DOM entirely.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog } from "@/components/ui/dialog";
import { formatDateTime } from "@/lib/utils";
import { mailroomThreadUrl } from "@/lib/use-send-history";
import { ExternalLink, Loader2 } from "lucide-react";

interface SentEmail {
    recipient: string;
    subject: string | null;
    sentAt: string;
    status: string;
    documents: string[];
    bodyHtml: string | null;
    bodyText: string | null;
    mailroom: { threadId: string; accountId: string } | null;
}

export function SentEmailDialog({
    open,
    onClose,
    jobId,
    ledgerId,
}: {
    open: boolean;
    onClose: () => void;
    jobId: string;
    ledgerId: string | null;
}) {
    const q = useQuery<SentEmail>({
        queryKey: ["sent-email", jobId, ledgerId],
        queryFn: () => api.get<SentEmail>(`/api/office-review/jobs/${jobId}/sent-email/${ledgerId}`),
        enabled: open && !!ledgerId,
        staleTime: Infinity, // A sent email never changes.
        retry: false,
    });

    const email = q.data;

    return (
        <Dialog open={open} onClose={onClose} title="The email we sent" className="max-w-2xl">
            {q.isLoading ? (
                <div className="flex items-center gap-2 py-6 text-sm text-foreground/60">
                    <Loader2 className="h-4 w-4 animate-spin" /> Opening…
                </div>
            ) : q.isError ? (
                <p className="py-4 text-sm text-foreground/70">
                    This email was sent before Groundwork started keeping a copy of the message, so there&apos;s nothing
                    to open. Who it went to and when are still on file above.
                </p>
            ) : email ? (
                <div className="space-y-3">
                    <div className="space-y-0.5 text-xs text-foreground/70">
                        <div>
                            <span className="font-semibold text-foreground/80">To</span> {email.recipient}
                        </div>
                        <div>
                            <span className="font-semibold text-foreground/80">Sent</span>{" "}
                            {formatDateTime(email.sentAt)}
                        </div>
                        {email.subject && (
                            <div>
                                <span className="font-semibold text-foreground/80">Subject</span> {email.subject}
                            </div>
                        )}
                        {email.documents.length > 0 && (
                            <div>
                                <span className="font-semibold text-foreground/80">Carried</span>{" "}
                                {email.documents.join(", ")}
                            </div>
                        )}
                    </div>

                    {email.bodyHtml ? (
                        <iframe
                            title={`Email sent to ${email.recipient}`}
                            srcDoc={email.bodyHtml}
                            sandbox=""
                            className="h-[460px] w-full rounded-md border bg-white"
                        />
                    ) : (
                        <pre className="max-h-[460px] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-xs">
                            {email.bodyText}
                        </pre>
                    )}

                    {email.mailroom && (
                        <Link
                            to={mailroomThreadUrl(email.mailroom)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                            Open the thread in Mailroom — including any reply
                            <ExternalLink className="h-3 w-3" />
                        </Link>
                    )}
                </div>
            ) : null}
        </Dialog>
    );
}
